import * as React from 'react'
// antd's OWN context, not a transcribed copy — the same reasoning as
// `../Input.shadcn.tsx`: a `<ConfigProvider componentDisabled>` cascade must
// reach a shadcn-mode field exactly as it reaches an antd-mode one. The
// FORM-ITEM status context is read by `../Input.shadcn.tsx` itself, one layer
// down, so it is not read again here.
import type { ValueType } from '@rc-component/mini-decimal'
import DisabledContext from 'antd/es/config-provider/DisabledContext'
import type { InputProps, InputRef } from 'antd/es/input'
import type { InputNumberProps } from 'antd/es/input-number'

import { DownOutlined, UpOutlined } from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

import { viewerDecimalSeparator } from './decimalSeparator'
import { useNumericMachine } from './numeric'
import { Input as ShadcnInput } from '../Input.shadcn'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `../InputNumber` seam. `./index.tsx` is the
 * dispatcher that chooses between this and antd's own InputNumber; nothing
 * else imports this file, and nothing reaches it until someone flips the
 * design toggle (`src/design-mode`).
 *
 * There is NO reference implementation for this one. `update-design` still
 * ships antd's InputNumber unchanged, and shadcn's registry has no number
 * input at all — which is why this port is shaped like `../Select`: the
 * VALUE MACHINE is a transcription of rc-input-number 9.0.0 (`./numeric.ts`,
 * which cites every rule it copies) and only the CHROME is new.
 *
 * THE CHROME IS `../Input.shadcn.tsx`, NOT A SECOND COPY OF IT. 79 of the
 * app's 185 tags render an `addonBefore`, an `addonAfter` or a `suffix` —
 * money labels and unit labels — and that markup, its focus ring, its
 * disabled treatment and its size handling already exist, tested, one file
 * over. Rendering through it is what makes a shadcn-mode number field and a
 * shadcn-mode text field the same object on screen, and it is why this file
 * is ~200 lines rather than ~500. The steppers ride in the `suffix` slot,
 * which puts them inside the field's border exactly where antd puts them.
 *
 * `.ant-input-number*` IS NOT RE-EMITTED — the `.ant-btn` situation again:
 * those rules carry height, padding, border and background, i.e. what this
 * seam replaces. The parts are keyed by `data-slot`, and the four live
 * stylesheets that reached into antd's names each grew a `[data-slot='…']`
 * selector beside the `.ant-input-number-…` one.
 *
 * NOT PORTED, deliberately, and measured:
 *   • `upHandler` / `downHandler` (rc's raw handler nodes). 0 call sites; the
 *     antd-level `controls={{ upIcon, downIcon }}` IS supported, and that is
 *     the prop antd itself documents.
 *   • rc's `useMobile()` handler suppression, which is UA sniffing. Replaced
 *     by `pointer: coarse`, which asks the real question — the steppers are
 *     hover-revealed, and a device with no hover cannot reveal them.
 *   • `stringMode` is CARRIED (`./numeric.ts` implements it) although 0 call
 *     sites use it, because dropping it would narrow the type antd declares.
 * ------------------------------------------------------------------------- */

/** rc-input-number/es/StepHandler.tsx — hold-to-repeat timings, verbatim. */
const STEP_INTERVAL = 200
const STEP_DELAY = 600

/**
 * The column's height, EXPLICIT rather than stretched — and it has to be.
 * `../Input.shadcn.tsx` wraps a `suffix` in its own centred span, so a
 * `self-stretch` here stretches inside THAT span (content height) and not to
 * the field. Browser-measured: an 18px column floating mid-field, with a
 * divider reaching neither edge. These are the affix wrapper's own heights
 * (h-7 / h-9 / h-11) less its 1px borders.
 */
const STEPPER_HEIGHT = {
  small: 'h-[26px]',
  middle: 'h-[34px]',
  large: 'h-[42px]',
} as const

interface StepHandlersProps {
  upIcon: React.ReactNode
  downIcon: React.ReactNode
  upDisabled: boolean
  downDisabled: boolean
  height: string
  onStep: (up: boolean) => void
}

const StepHandlers = ({
  upIcon,
  downIcon,
  upDisabled,
  downDisabled,
  height,
  onStep,
}: StepHandlersProps) => {
  const stepTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>()
  const onStepRef = React.useRef(onStep)
  onStepRef.current = onStep

  const stopStep = () => clearTimeout(stepTimeoutRef.current)

  React.useEffect(() => stopStep, [])

  const startStep = (
    event: React.MouseEvent<HTMLButtonElement>,
    up: boolean
  ) => {
    // Without this the mousedown steals focus from the input, and rc's own
    // `inputRef.focus()` inside the step would fight the browser for it.
    event.preventDefault()
    stopStep()
    onStepRef.current(up)

    const loopStep = () => {
      onStepRef.current(up)
      stepTimeoutRef.current = setTimeout(loopStep, STEP_INTERVAL)
    }
    stepTimeoutRef.current = setTimeout(loopStep, STEP_DELAY)
  }

  const handlerClass =
    'flex h-1/2 w-full cursor-pointer items-center justify-center text-[0.6em] text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground aria-disabled:cursor-not-allowed aria-disabled:opacity-40 aria-disabled:hover:bg-transparent'

  return (
    <span
      data-slot="input-number-handlers"
      // Hover-revealed, as antd's are. `pointer: coarse` hides them outright:
      // a touch device cannot hover, so a permanently visible pair would just
      // eat the field's width beside a keyboard that already steps.
      className={cn(
        '-mr-3 flex w-5 shrink-0 flex-col overflow-hidden rounded-r-[5px] border-l border-input opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 [@media(pointer:coarse)]:hidden',
        height
      )}
    >
      <button
        type="button"
        tabIndex={-1}
        data-slot="input-number-handler-up"
        aria-label="Increase Value"
        aria-disabled={upDisabled}
        className={cn(handlerClass, 'border-b border-input')}
        onMouseDown={(event) => startStep(event, true)}
        onMouseUp={stopStep}
        onMouseLeave={stopStep}
      >
        {upIcon}
      </button>
      <button
        type="button"
        tabIndex={-1}
        data-slot="input-number-handler-down"
        aria-label="Decrease Value"
        aria-disabled={downDisabled}
        className={handlerClass}
        onMouseDown={(event) => startStep(event, false)}
        onMouseUp={stopStep}
        onMouseLeave={stopStep}
      >
        {downIcon}
      </button>
    </span>
  )
}

/**
 * BOTH handlers run, caller's first — which is what the antd branch does and
 * therefore what this seam owes. rc-input-number puts the call site's props on
 * the `<input>` and hangs its own listeners on the holder element around it, so
 * a synthetic event fires the call site's handler at the target and rc's on the
 * way up. Here there is one element, so the composition is explicit: letting
 * either side win silently drops the other, and live call sites depend on both
 * (a save-on-blur row, a `Form.Item` under `validateTrigger='onBlur'`, a
 * key-filtering `onKeyDown`, and the machine's own Enter-flush / arrow-step /
 * flush-on-blur). A caller's `preventDefault()` does not suppress the machine
 * half, exactly as it does not stop the event reaching rc's holder.
 */
const composeHandler =
  <E,>(caller: ((event: E) => void) | undefined, machine: (event: E) => void) =>
  (event: E) => {
    caller?.(event)
    machine(event)
  }

const InputNumberBase = React.forwardRef<
  HTMLInputElement,
  InputNumberProps<ValueType>
>((props, ref) => {
  const {
    // ---- value machine
    value,
    defaultValue,
    min,
    max,
    step,
    precision,
    decimalSeparator,
    stringMode,
    keyboard,
    changeOnBlur,
    changeOnWheel,
    formatter,
    parser,
    onChange,
    onInput,
    onPressEnter,
    onStep,
    // ---- event handlers the value machine also owns; see `composeHandler`
    onKeyDown: callerKeyDown,
    onKeyUp: callerKeyUp,
    onFocus: callerFocus,
    onBlur: callerBlur,
    onCompositionStart: callerCompositionStart,
    onCompositionEnd: callerCompositionEnd,
    onBeforeInput: callerBeforeInput,
    // ---- chrome
    className,
    style,
    size,
    status,
    variant,
    bordered,
    disabled: disabledProp,
    readOnly,
    prefix,
    suffix,
    addonBefore,
    addonAfter,
    controls = true,
    // ---- antd-only props that must never reach the DOM
    prefixCls: _prefixCls,
    rootClassName: _rootClassName,
    classNames: _classNames,
    upHandler: _upHandler,
    downHandler: _downHandler,
    ...rest
  } = props

  const contextDisabled = React.useContext(DisabledContext)
  const disabled = disabledProp ?? contextDisabled
  // `../Input.shadcn.tsx` exposes antd's `InputRef` handle, not the element;
  // the value machine needs the ELEMENT (caret restore, focus after a step),
  // so the handle is unwrapped into a plain ref here. antd's own InputNumber
  // ref is the `<input>` itself, which is what the call sites that take one
  // were written against.
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const captureInput = React.useCallback((handle: InputRef | null) => {
    inputRef.current = handle?.input ?? null
  }, [])
  React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

  /* THE ONE BEHAVIOUR DIVERGENCE FROM THE ANTD HALF, AND IT IS DELIBERATE.
   * The antd branch reads a typed `375,50` as 37550 on a Danish or Finnish
   * tenant — a 100x error on every money field — because rc-input-number's
   * default parser deletes a comma it was not told about. Here the separator
   * defaults to the VIEWER'S, so the same keystrokes give 375.5. The antd
   * branch is deliberately left as it is; `./decimalSeparator.ts` carries the
   * whole reasoning and the captain's ruling. READ IT BEFORE CHANGING THIS
   * LINE — removing the fallback re-introduces the 100x error. */
  const mergedDecimalSeparator = decimalSeparator ?? viewerDecimalSeparator()

  const machine = useNumericMachine({
    value,
    defaultValue,
    min,
    max,
    step,
    precision,
    decimalSeparator: mergedDecimalSeparator,
    stringMode,
    disabled,
    readOnly,
    keyboard,
    changeOnBlur,
    changeOnWheel,
    formatter: formatter as never,
    parser: parser as never,
    onChange: onChange as never,
    onInput,
    onPressEnter,
    onStep: onStep as never,
    inputRef: inputRef as React.RefObject<HTMLInputElement>,
  })

  const showControls = controls !== false
  const controlIcons = typeof controls === 'object' ? controls : undefined

  const steppers = showControls ? (
    <StepHandlers
      upIcon={controlIcons?.upIcon ?? <UpOutlined />}
      downIcon={controlIcons?.downIcon ?? <DownOutlined />}
      upDisabled={machine.upDisabled}
      downDisabled={machine.downDisabled}
      height={STEPPER_HEIGHT[size ?? 'middle']}
      onStep={machine.doStep}
    />
  ) : null

  // A call site's own `suffix` (11 tags: units, '%', a currency) keeps its
  // place NEXT TO the text; the steppers sit outermost, against the border,
  // which is where antd draws them.
  const mergedSuffix =
    steppers && suffix ? (
      <>
        {suffix}
        {steppers}
      </>
    ) : (
      steppers ?? suffix
    )

  return (
    <ShadcnInput
      {...(rest as InputProps)}
      ref={captureInput}
      // `group` is what the hover-revealed steppers key off. It lands on the
      // affix wrapper when there are no addons and on the addon group when
      // there are — either way an ancestor of the suffix, so one class covers
      // both shapes.
      className={cn(
        'group',
        // antd paints `.ant-input-number-out-of-range` red when a CONTROLLED
        // value sits outside min/max; without this the new design flags the
        // same condition nowhere. The attribute rides on the `<input>` (the
        // `aria-invalid` precedent) and the colour on this root, which is the
        // affix wrapper, the addon group or the input itself depending on the
        // props — in every one of those shapes the input's text inherits it.
        machine.outOfRange && 'text-destructive',
        className
      )}
      data-out-of-range={machine.outOfRange || undefined}
      style={style}
      size={size}
      status={status}
      variant={variant}
      bordered={bordered}
      disabled={disabled}
      readOnly={readOnly}
      prefix={prefix}
      suffix={mergedSuffix}
      addonBefore={addonBefore}
      addonAfter={addonAfter}
      value={machine.inputValue}
      onChange={machine.handlers.onChange}
      onKeyDown={composeHandler(callerKeyDown, machine.handlers.onKeyDown)}
      onKeyUp={composeHandler(callerKeyUp, machine.handlers.onKeyUp)}
      onFocus={composeHandler(callerFocus, machine.handlers.onFocus)}
      onBlur={composeHandler(callerBlur, machine.handlers.onBlur)}
      onCompositionStart={composeHandler(
        callerCompositionStart,
        machine.handlers.onCompositionStart
      )}
      onCompositionEnd={composeHandler(
        callerCompositionEnd,
        machine.handlers.onCompositionEnd
      )}
      onBeforeInput={composeHandler(
        callerBeforeInput,
        machine.handlers.onBeforeInput
      )}
      // antd's own spinbutton semantics, transcribed: a screen reader has to
      // be told this is a number field and what its bounds are.
      role="spinbutton"
      aria-valuemin={min as number | undefined}
      aria-valuemax={max as number | undefined}
      aria-valuenow={machine.ariaValueNow as unknown as number}
      autoComplete="off"
    />
  )
})

InputNumberBase.displayName = 'InputNumber'

export const InputNumber = InputNumberBase as unknown as (<
  T extends ValueType = ValueType
>(
  props: InputNumberProps<T> & React.RefAttributes<HTMLInputElement>
) => React.ReactElement) & { displayName?: string }
